import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import useSound from 'use-sound';
import sounds from '../sounds.mp3';
import { useAuth } from '../contexts/AuthContext';
import { SCREEN_NAME_PATTERN, getScreenNameHint } from '../screenNameValidation';

const soundMap = {
  register: [2300, 2000],
  error: [23700, 1000],
};

export default function Register() {
  const [screenName, setScreenName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [play] = useSound(sounds, { interrupt: true, sprite: soundMap });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Step 1: Get registration options
      const optRes = await fetch('/api/passkey/register-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screen_name: screenName, email }),
      });
      const optData = await optRes.json();
      if (!optRes.ok) {
        setError(optData.error);
        play({ id: 'error' });
        return;
      }

      // Step 2: Browser WebAuthn ceremony
      const attestationResponse = await startRegistration({ optionsJSON: optData.options });

      // Step 3: Verify with server
      const verifyRes = await fetch('/api/passkey/register-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeToken: optData.challengeToken,
          attestationResponse,
        }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        setError(verifyData.error);
        play({ id: 'error' });
        return;
      }

      play({ id: 'register' });
      login(verifyData.token, verifyData.user);
      navigate('/');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey creation was cancelled.');
      } else {
        setError('Registration failed. Try again.');
      }
      play({ id: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const nameHint = screenName.length > 0 ? getScreenNameHint(screenName) : null;

  return (
    <div className="buddy-info window">
      <div className="title-bar">
        <span className="title-bar-text">
          <span>New Screen Name Registration</span>
        </span>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={() => navigate('/')}></button>
        </div>
      </div>
      <div className="window-body">
        <form onSubmit={handleSubmit}>
          <div className="field-row-stacked" style={{ width: '100%' }}>
            <label htmlFor="reg-sn">Screen Name:</label>
            <input
              id="reg-sn"
              type="text"
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              maxLength={16}
              pattern={SCREEN_NAME_PATTERN}
              title="3-16 characters; start with a letter; use letters, numbers, and underscores only"
              required
            />
            {nameHint && <small style={{ color: '#c00' }}>{nameHint}</small>}
          </div>
          <div className="field-row-stacked" style={{ width: '100%' }}>
            <label htmlFor="reg-email">Email:</label>
            <input
              id="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <p style={{ fontSize: 11, color: '#666', margin: '4px 0' }}>
            🔑 You'll be prompted to create a passkey (fingerprint, face, or security key) to secure your account.
          </p>
          {error && <p style={{ color: '#c00', fontSize: 12 }}>{error}</p>}
          <div className="field-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Registering...' : 'Register with Passkey'}
            </button>
            <button type="button" onClick={() => navigate('/login')} style={{ marginLeft: 4 }}>
              Sign In Instead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
